/**
 * Apertura y cierre de los datos locales de una cuenta.
 *
 * Cada cuenta tiene su base (`fitness-<userId>`), que se abre al saber quién
 * ha entrado y se cierra al salir. Cerrar sesión **no borra nada**: lo que
 * quedara sin subir se queda en la base de esa cuenta y sale cuando vuelva a
 * entrar. Sólo al borrar la cuenta se borra su base.
 *
 * El orden importa:
 *
 * - Al abrir, primero se ata el cliente de la API a la cuenta, después se
 *   reparte la base compartida de antes (puede preguntar al servidor) y por
 *   último se abre la base de la cuenta, antes de pintar nada privado.
 * - Al cerrar, primero se detiene la cola, después se suelta la cuenta del
 *   cliente (corta lo que seguía en vuelo y no deja salir nada con token) y
 *   se espera a que termine el envío; sólo entonces se cierra la base. Quien
 *   cierra sesión espera a todo esto antes de tocar el token, así que nada de
 *   una cuenta puede salir con el token de la siguiente.
 *
 * La regla para repartir la base compartida está en `@fitness/core`
 * (`claimLegacyStore`).
 */
import {
  captureLegacyOwner,
  claimLegacyStore,
  lastMealKey,
  LAST_MEAL_KEY,
  type LegacyRecord,
  type LegacyRecordStore,
  type LegacyStore,
  type WitnessResult,
} from '@fitness/core';
import Dexie from 'dexie';

import { ApiClientError, api, bindApiAccount } from '@/lib/api';
import {
  FitnessDB,
  LEGACY_DB_NAME,
  closeUserDb,
  localDbUserId,
  openUserDb,
  userDbName,
} from './db';
import { flushPending, haltSync } from './syncQueue';

/** Dónde se anota el dueño de la base compartida de antes (ver `@fitness/core`). */
export const LEGACY_RECORD_KEY = 'fitness.legacyDb';

/** Caché de Workbox con las respuestas de `/v1/me` (ver `vite.config.ts`). */
const ME_CACHE_NAME = 'fitness-api-me';

/** Lo que tarda como mucho en borrarse una base antes de seguir sin esperar. */
const DELETE_TIMEOUT_MS = 2000;

// --- localStorage -----------------------------------------------------------

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const legacyRecords: LegacyRecordStore = {
  async read() {
    try {
      const raw = storage()?.getItem(LEGACY_RECORD_KEY);
      if (raw == null) return null;
      const parsed = JSON.parse(raw) as Partial<LegacyRecord> | null;
      if (parsed === null || typeof parsed !== 'object') return null;
      return { owner: typeof parsed.owner === 'string' ? parsed.owner : null };
    } catch {
      return null;
    }
  },
  async write(record) {
    try {
      storage()?.setItem(LEGACY_RECORD_KEY, JSON.stringify(record));
    } catch {
      // Sin almacenamiento la base antigua queda sin dueño anotado: se decide
      // preguntando al servidor.
    }
  },
  async clear() {
    try {
      storage()?.removeItem(LEGACY_RECORD_KEY);
    } catch {
      // Ídem.
    }
  },
};

// --- Base compartida de antes ------------------------------------------------

/** Borra una base sin quedarse esperando si otra pestaña la tiene abierta. */
async function deleteDatabase(name: string): Promise<void> {
  await Promise.race([
    Dexie.delete(name).catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, DELETE_TIMEOUT_MS)),
  ]);
}

/** La base `fitness` vista como {@link LegacyStore}. Se abre al primer uso. */
function legacyStore(): LegacyStore & { close: () => void } {
  let opened: FitnessDB | null = null;
  const legacy = (): FitnessDB => {
    opened ??= new FitnessDB(LEGACY_DB_NAME);
    return opened;
  };
  const close = (): void => {
    opened?.close();
    opened = null;
  };

  return {
    close,
    exists: () => Dexie.exists(LEGACY_DB_NAME),

    async isEmpty() {
      const db = legacy();
      const counts = await Promise.all(db.tables.map((table) => table.count()));
      return counts.every((count) => count === 0);
    },

    async sessions() {
      const rows = await legacy().sessions.toArray();
      return rows.map((row) => ({ clientId: row.clientId, synced: row.syncedAt !== null }));
    },

    async moveInto(userId) {
      const source = legacy();
      const target = new FitnessDB(userDbName(userId));
      try {
        for (const table of source.tables) {
          const rows = await table.toArray();
          if (rows.length === 0) continue;
          const destination = target.table(table.name);
          const keyPath = String(table.schema.primKey.keyPath);
          const keys = rows.map((row) => (row as Record<string, unknown>)[keyPath] as string);
          const existing = await destination.bulkGet(keys);
          // Lo que la cuenta ya tenga manda: nunca se pisa con la copia antigua.
          const missing = rows.filter((_, index) => existing[index] === undefined);
          if (missing.length > 0) await destination.bulkAdd(missing);
        }
      } finally {
        target.close();
      }
      // Copiado: se vacía la antigua para que nada se pueda copiar dos veces.
      await source.transaction('rw', source.tables, async () => {
        await Promise.all(source.tables.map((table) => table.clear()));
      });
    },

    async destroy() {
      close();
      await deleteDatabase(LEGACY_DB_NAME);
    },
  };
}

/** ¿Tiene el servidor esta sesión para la cuenta que entra? */
async function lookupSession(clientId: string): Promise<WitnessResult> {
  try {
    await api.workouts.get(clientId);
    return 'found';
  } catch (error) {
    if (error instanceof ApiClientError && (error.status === 404 || error.status === 403)) {
      return 'missing';
    }
    return 'unknown';
  }
}

/**
 * Anota el dueño de la base compartida de antes, si la hay: la cuenta de la
 * pista de sesión. Se llama al arrancar, antes de que nada escriba o borre la
 * pista, y sólo hace algo la primera vez.
 */
export async function rememberLegacyOwner(hintUserId: string | null): Promise<void> {
  const store = legacyStore();
  try {
    await captureLegacyOwner(store, legacyRecords, hintUserId);
  } catch {
    // Sin IndexedDB no hay base antigua que repartir.
  } finally {
    store.close();
  }
}

/** Reparte la base compartida de antes al entrar `userId` (ver `claimLegacyStore`). */
async function claimLegacyDb(userId: string): Promise<void> {
  const store = legacyStore();
  try {
    await claimLegacyStore({ userId, store, records: legacyRecords, lookupSession });
  } catch {
    // Se vuelve a intentar en el próximo inicio de sesión; la base antigua
    // sigue intacta o, si llegó a copiarse, ya vacía.
  } finally {
    store.close();
  }
}

// --- Cuenta activa -------------------------------------------------------------

/**
 * Abre los datos locales de una cuenta: ata el cliente de la API, reparte la
 * base de antes si le toca y abre su base. Hay que esperarla antes de pintar
 * el árbol privado.
 */
export async function openAccount(userId: string): Promise<void> {
  if (localDbUserId() !== null && localDbUserId() !== userId) await closeAccount();
  bindApiAccount(userId);
  await claimLegacyDb(userId);
  openUserDb(userId);
  // La preferencia de comida ya va por cuenta; la compartida de antes sobra.
  try {
    storage()?.removeItem(LAST_MEAL_KEY);
  } catch {
    // Sin almacenamiento no hay nada que quitar.
  }
}

/** Sube lo que la cuenta tuviera pendiente nada más entrar, sin esperar al backoff. */
export function flushAccount(): void {
  void flushPending({ force: true });
}

/**
 * Cierra los datos locales de la cuenta activa sin borrar nada. Hay que
 * esperarla **antes** de cerrar la sesión en el servidor o de iniciar otra.
 */
export async function closeAccount(): Promise<void> {
  const halted = haltSync();
  bindApiAccount(null);
  await halted;
  closeUserDb();
}

/** Borra la caché del service worker con las respuestas de `/v1/me`. */
export async function forgetCachedProfile(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete(ME_CACHE_NAME);
  } catch {
    // Sin Cache Storage (contexto no seguro) no hay nada que borrar.
  }
}

/**
 * Borra todo lo que el navegador guarda de una cuenta: su base (sesiones,
 * series, diario y cola), la base compartida de antes si era suya, su
 * preferencia de comida y la caché de `/v1/me`. Se usa al borrar la cuenta:
 * lo pendiente se tira a propósito, porque el servidor ya no tiene a quién
 * atribuírselo.
 */
export async function wipeAccountData(userId: string): Promise<void> {
  if (localDbUserId() === userId) await closeAccount();
  await deleteDatabase(userDbName(userId));
  const record = await legacyRecords.read();
  if (record?.owner === userId) {
    await deleteDatabase(LEGACY_DB_NAME);
    await legacyRecords.clear();
  }
  try {
    storage()?.removeItem(lastMealKey(userId));
  } catch {
    // Ídem.
  }
  await forgetCachedProfile();
}
